import React from 'react';
import { 
  Paper, 
  Box, 
  Typography, 
  Skeleton,
  Stack,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert
} from '@mui/material';
import { 
  Analytics,
  FileDownload,
  Schedule
} from '@mui/icons-material';

interface ResultsLoadingSkeletonProps {
  phase: 'completing' | 'preparing-results' | 'loading-data' | 'processing-file';
  progress?: number;
  message?: string;
  showDownloadPrep?: boolean;
}

const ResultsLoadingSkeleton: React.FC<ResultsLoadingSkeletonProps> = ({
  phase,
  progress,
  message,
  showDownloadPrep = false
}) => {
  const getPhaseInfo = () => {
    switch (phase) {
      case 'completing':
        return {
          title: 'Finalizing Processing',
          subtitle: 'Completing keyword classification and generating results...',
          icon: <Schedule color="primary" />
        };
      case 'preparing-results':
        return {
          title: 'Preparing Results',
          subtitle: 'Organizing classified keywords and generating output files...',
          icon: <Analytics color="primary" />
        };
      case 'loading-data':
        return {
          title: 'Loading Results',
          subtitle: 'Retrieving your classified keyword data...',
          icon: <FileDownload color="primary" />
        };
      case 'processing-file':
        return {
          title: 'Processing Results File',
          subtitle: 'Parsing and preparing data for visualization...',
          icon: <Analytics color="primary" />
        };
      default:
        return {
          title: 'Loading',
          subtitle: 'Please wait...',
          icon: <Schedule color="primary" />
        };
    }
  };

  const phaseInfo = getPhaseInfo();

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      {/* Header with phase information */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        {phaseInfo.icon}
        <Box>
          <Typography variant="h6" color="primary.main">
            {phaseInfo.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {message || phaseInfo.subtitle}
          </Typography>
        </Box>
      </Box>

      {/* Progress indicator */}
      {progress !== undefined && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {Math.round(progress)}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress}
            sx={{ 
              height: 8, 
              borderRadius: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 1,
                transition: 'transform 0.3s ease-in-out'
              }
            }}
          />
        </Box>
      )}

      {/* Download preparation info */}
      {showDownloadPrep && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Results will be ready for download shortly. The page will automatically update when processing is complete.
        </Alert>
      )}

      {/* Skeleton for results visualization */}
      <Box>
        {/* Results header skeleton */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="text" width={250} height={32} />
          <Skeleton variant="rectangular" width={140} height={36} sx={{ borderRadius: 1 }} />
        </Box>

        {/* Stats cards skeleton */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center' }}>
                  <Skeleton variant="text" width={80} height={40} sx={{ mx: 'auto', mb: 1 }} />
                  <Skeleton variant="text" width={100} height={20} sx={{ mx: 'auto' }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Chart skeleton */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Skeleton variant="text" width={200} height={24} sx={{ mb: 2 }} />
          <Stack spacing={1}>
            {[1, 2, 3, 4, 5].map((item) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="rectangular" width={12} height={20} />
                <Skeleton variant="text" width={150} height={20} />
                <Skeleton variant="text" width={60} height={20} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="rectangular" height={8} sx={{ borderRadius: 1 }} />
                </Box>
              </Box>
            ))}
          </Stack>
        </Paper>

        {/* Table skeleton */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Skeleton variant="text" width={180} height={24} sx={{ mb: 2 }} />
          
          {/* Table header */}
          <Box sx={{ display: 'flex', gap: 2, mb: 1, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Skeleton variant="text" width={120} height={20} />
            <Skeleton variant="text" width={100} height={20} />
            <Skeleton variant="text" width={80} height={20} />
            <Skeleton variant="text" width={100} height={20} />
          </Box>
          
          {/* Table rows */}
          {[1, 2, 3, 4, 5].map((item) => (
            <Box key={item} sx={{ display: 'flex', gap: 2, mb: 1, py: 1 }}>
              <Skeleton variant="text" width={120} height={20} />
              <Skeleton variant="text" width={100} height={20} />
              <Skeleton variant="rectangular" width={80} height={20} sx={{ borderRadius: 1 }} />
              <Skeleton variant="text" width={100} height={20} />
            </Box>
          ))}
        </Paper>

        {/* Shimmer effect overlay */}
        <Box
          sx={{
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              height: '100%',
              width: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
              animation: 'shimmer 2s infinite',
            },
            '@keyframes shimmer': {
              '0%': { left: '-100%' },
              '100%': { left: '100%' }
            }
          }}
        />
      </Box>
    </Paper>
  );
};

export default ResultsLoadingSkeleton;